import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { X, Plus, MessageCircle } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { RadarPerspective } from "@/components/charts/RadarPerspective";
import { useApp } from "@/context/AppContext";
import { useCompare } from "@/lib/useCompare";
import { formatPriceRange } from "@/lib/format";
import { VERTICALS } from "@/lib/perspective";
import { imgSrc } from "@/lib/api";

const ROWS = [
  { key: "priceRange", label: "Price", fmt: (p) => formatPriceRange(p.priceMin, p.priceMax) },
  { key: "developer", label: "Developer" },
  { key: "location", label: "Location", fmt: (p) => `${p.location}, ${p.microMarket}` },
  { key: "configuration", label: "Configuration", fmt: (p) => p.configuration.join(", ") },
  { key: "area", label: "Area" },
  { key: "possession", label: "Possession" },
  { key: "rera", label: "RERA", fmt: (p) => p.rera.slice(0, 22) + "…" },
  { key: "amenities", label: "Amenities", fmt: (p) => `${p.amenities.length} listed` },
];

const BAR_FILLS = ["linear-gradient(90deg,#9ED3B8,var(--accent-gold))", "#0D2B21", "#66756D"];

export default function Compare() {
  const { data } = useApp();
  const { ids, remove, clear } = useCompare();
  const selected = ids.map((id) => data.properties.find((p) => p.id === id)).filter(Boolean);
  const commonVertical = useMemo(() => {
    if (!selected.length) return "living";
    const counts = {};
    selected.forEach((p) => p.category.forEach((c) => (counts[c] = (counts[c] || 0) + 1)));
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || "living";
  }, [selected]);
  const [vtab, setVtab] = useState(null);
  const activeV = vtab || commonVertical;
  const vertical = VERTICALS[activeV];

  const radarData = vertical.criteria.map((c) => {
    const row = { criterion: c.label };
    selected.forEach((p, i) => { row[`p${i}`] = p.scores?.[c.key] ?? 60; });
    row.value = selected[0]?.scores?.[c.key] ?? 60;
    if (selected[1]) row.value2 = selected[1].scores?.[c.key] ?? 60;
    return row;
  });

  return (
    <div className="container-arch pt-32 pb-24">
      <Reveal>
        <div className="overline">Compare Perspectives</div>
        <h1 className="mt-3 font-serif text-4xl font-light text-[var(--text-primary)] sm:text-5xl">Compare, without a single "best".</h1>
        <p className="mt-3 max-w-xl text-[var(--text-muted)]">Select up to 3 properties. We highlight differences in perspective — the right one depends on you.</p>
      </Reveal>

      {selected.length === 0 ? (
        <div className="mt-14 rounded-2xl border border-dashed border-[var(--border-subtle)] py-20 text-center" data-testid="compare-empty">
          <p className="font-serif text-2xl text-[var(--text-primary)]">Nothing to compare yet.</p>
          <p className="mt-2 text-sm text-[var(--text-muted)]">Add properties using the compare icon on any property.</p>
          <Link to="/properties" className="btn-gold mt-6">Browse Properties</Link>
        </div>
      ) : (
        <>
          <div className="mt-10 overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse" data-testid="compare-table">
              <thead>
                <tr>
                  <th className="w-40 p-3 text-left align-bottom text-xs uppercase tracking-widest text-[var(--text-muted)]">Attribute</th>
                  {selected.map((p, i) => (
                    <th key={p.id} className="p-3 text-left align-bottom">
                      <div className="arch-card overflow-hidden p-0">
                        <div className="relative h-28"><img src={imgSrc(p.images[0])} alt={p.project} className="h-full w-full object-cover" />
                          <button onClick={() => remove(p.id)} data-testid={`compare-remove-${p.id}`} className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/50 text-white"><X className="h-4 w-4" /></button>
                        </div>
                        <div className="p-3"><div className="font-serif text-lg text-[var(--text-primary)]">{p.project}</div></div>
                      </div>
                    </th>
                  ))}
                  {selected.length < 3 && (
                    <th className="p-3 align-middle"><Link to="/properties" className="flex h-full min-h-[160px] w-40 flex-col items-center justify-center rounded-2xl border border-dashed border-[var(--border-subtle)] text-[var(--text-muted)] hover:border-[var(--accent-gold)] hover:text-[var(--accent-gold)]"><Plus className="h-6 w-6" /><span className="mt-1 text-xs">Add</span></Link></th>
                  )}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((r) => (
                  <tr key={r.key} className="border-t border-[var(--border-subtle)]">
                    <td className="p-3 text-sm font-medium text-[var(--text-muted)]">{r.label}</td>
                    {selected.map((p) => <td key={p.id} className="p-3 text-sm text-[var(--text-primary)]">{r.fmt ? r.fmt(p) : p[r.key]}</td>)}
                    {selected.length < 3 && <td />}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-14">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div><div className="overline">Comparison graphs</div><h2 className="mt-2 font-serif text-3xl font-light text-[var(--text-primary)]">{vertical.perspectiveLabel}</h2></div>
              <div className="inline-flex rounded-full border border-[var(--border-subtle)] p-1">
                {Object.values(VERTICALS).map((v) => (
                  <button key={v.key} onClick={() => setVtab(v.key)} data-testid={`compare-vtab-${v.key}`} className={`rounded-full px-4 py-1.5 text-xs font-medium transition ${activeV === v.key ? "bg-[var(--accent-gold)] text-[#071711]" : "text-[var(--text-muted)]"}`}>{v.name}</button>
                ))}
              </div>
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-[1.1fr_1fr]">
              <div className="arch-card p-6"><RadarPerspective data={radarData} secondName={selected[1]?.project} /></div>
              <div className="arch-card p-6">
                <div className="space-y-5">
                  {vertical.criteria.map((c) => (
                    <div key={c.key}>
                      <div className="mb-2 text-sm font-medium text-[var(--text-primary)]">{c.label}</div>
                      <div className="space-y-1.5">
                        {selected.map((p, i) => {
                          const val = p.scores?.[c.key] ?? 60;
                          return (
                            <div key={p.id} className="flex items-center gap-2">
                              <span className="w-24 truncate text-xs text-[var(--text-muted)]">{p.project}</span>
                              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--border-subtle)]">
                                <div className="h-full rounded-full" style={{ width: `${val}%`, background: BAR_FILLS[i] || BAR_FILLS[2] }} />
                              </div>
                              <span className="w-7 text-right text-xs text-[var(--text-primary)]">{val}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <p className="mt-4 text-xs text-[var(--text-muted)]">Illustrative Archedia evaluation data. Differences are shown to inform perspective — not to declare one property universally best.</p>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <Link to="/contact" data-testid="compare-discuss" className="btn-gold"><MessageCircle className="h-4 w-4" /> Discuss This Comparison With Archedia</Link>
            <button onClick={clear} className="btn-outline-gold">Clear comparison</button>
          </div>
        </>
      )}
    </div>
  );
}
